// Tipos e parsers do boleto único (aglutinação de títulos em atraso no Protheus).
// O Protheus devolve strings com padding e datas no formato yyyyMMdd.

export const texto = (v: unknown): string | null => {
  const t = String(v ?? "").trim();
  return t === "" ? null : t;
};

export const decimal = (v: unknown): number =>
  typeof v === "number" ? v : Number.parseFloat(String(v ?? "")) || 0;

export const data = (v: unknown): Date | null => {
  const t = texto(v);
  if (!t || t.length !== 8) return null;
  const d = new Date(`${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6)}`);
  return isNaN(+d) ? null : d;
};

// ----------------------------------------------------------------------

export interface TituloBoletoUnico {
  prefixo: string;
  numero: string;
  parcela: string;
  tipo: string;
}

export interface BoletoUnicoParams {
  contract_number: string;
  titulos: TituloBoletoUnico[];
}

export interface ItemSimulacaoBoletoUnico {
  item: string;
  recno: number | null;
  prefixo: string;
  numero: string;
  parcela: string;
  tipo: string;
  vencimentoOriginal: Date | null;
  diasAtraso: number;
  valorOriginal: number;
  saldoOriginal: number;
  encargos: number;
  juros: number;
  multa: number;
  saldoNegociado: number;
}

export interface SimulacaoBoletoUnico {
  contrato: string;
  tipoNegociacao: string | null;
  cliente: string | null;
  loja: string | null;
  quantidadeTitulos: number;
  valorOriginal: number;
  valorNegociado: number;
  vencimentoSugerido: Date | null;
  dataSimulacao: Date | null;
  simulado: boolean;
  itens: ItemSimulacaoBoletoUnico[];
}

export interface ItemNegociacaoBoletoUnico {
  item: string;
  titulo: string;
  saldo: number;
  status: string | null;
}

export interface NegociacaoBoletoUnico {
  codigo: string;
  contrato: string;
  status: string | null;
  statusDescricao: string | null;
  valor: number;
  vencimento: Date | null;
  dataLimite: Date | null;
  dataGeracao: Date | null;
  dataPagamento: Date | null;
  dataBaixa: Date | null;
  nossoNumero: string | null;
  linhaDigitavel: string | null;
  codigoBarras: string | null;
  qrcodePix: string | null;
  pixTxid: string | null;
  linkBoleto: string | null;
  pagamentoConfirmado: boolean;
  aguardandoBaixa: boolean;
  itens: ItemNegociacaoBoletoUnico[];
}

// ----------------------------------------------------------------------

export function itemSimulacaoFromJson(json: any): ItemSimulacaoBoletoUnico {
  return {
    item: texto(json?.item) ?? "",
    recno: typeof json?.recno === "number" ? json.recno : null,
    prefixo: texto(json?.prefixo) ?? "",
    numero: texto(json?.numero) ?? "",
    parcela: texto(json?.parcela) ?? "",
    tipo: texto(json?.tipo) ?? "",
    vencimentoOriginal: data(json?.vencimentoOriginal),
    diasAtraso: Number(json?.diasAtraso) || 0,
    valorOriginal: decimal(json?.valorOriginal),
    saldoOriginal: decimal(json?.saldoOriginal),
    encargos: decimal(json?.encargos),
    juros: decimal(json?.juros),
    multa: decimal(json?.multa),
    saldoNegociado: decimal(json?.saldoNegociado),
  };
}

export function simulacaoFromJson(json: any): SimulacaoBoletoUnico {
  return {
    contrato: texto(json?.contrato) ?? "",
    tipoNegociacao: texto(json?.tipoNegociacao),
    cliente: texto(json?.cliente),
    loja: texto(json?.loja),
    quantidadeTitulos: Number(json?.quantidadeTitulos) || 0,
    valorOriginal: decimal(json?.valorOriginal),
    valorNegociado: decimal(json?.valorNegociado),
    vencimentoSugerido: data(json?.vencimentoSugerido),
    dataSimulacao: data(json?.dataSimulacao),
    simulado: json?.simulado === true,
    itens: Array.isArray(json?.itens) ? json.itens.map(itemSimulacaoFromJson) : [],
  };
}

export function itemNegociacaoFromJson(json: any): ItemNegociacaoBoletoUnico {
  return {
    item: texto(json?.item) ?? "",
    titulo: texto(json?.titulo) ?? "",
    saldo: decimal(json?.saldo),
    status: texto(json?.status),
  };
}

export function negociacaoFromJson(json: any): NegociacaoBoletoUnico {
  return {
    codigo: texto(json?.codigo) ?? "",
    contrato: texto(json?.contrato) ?? "",
    status: texto(json?.status),
    statusDescricao: texto(json?.statusDesc),
    valor: decimal(json?.valor),
    vencimento: data(json?.vencimento),
    dataLimite: data(json?.dataLimite),
    dataGeracao: data(json?.dataGeracao),
    dataPagamento: data(json?.dataPagamento),
    dataBaixa: data(json?.dataBaixa),
    nossoNumero: texto(json?.nossoNumero),
    // linhaDigit é o nome legado devolvido pelo Protheus.
    linhaDigitavel: texto(json?.linhaDigitavel) ?? texto(json?.linhaDigit),
    codigoBarras: texto(json?.codigoBarras),
    qrcodePix: texto(json?.qrcodePix),
    pixTxid: texto(json?.pixTxid),
    linkBoleto: texto(json?.linkBoleto),
    pagamentoConfirmado: json?.pagamentoConfirmado === true,
    aguardandoBaixa: json?.aguardandoBaixa === true,
    itens: Array.isArray(json?.itens) ? json.itens.map(itemNegociacaoFromJson) : [],
  };
}

// ----------------------------------------------------------------------

export const hasBoleto = (n: NegociacaoBoletoUnico) =>
  n.linhaDigitavel !== null || n.linkBoleto !== null;

export const hasPix = (n: NegociacaoBoletoUnico) => n.qrcodePix !== null;

/** Juros e encargos do agregado — não é a soma de juros + multa dos itens. */
export const encargosTotais = (s: SimulacaoBoletoUnico) => s.valorNegociado - s.valorOriginal;

